
import { Component, Mixins } from "vue-property-decorator";
import NavHeader from "@/components/NavHeader.vue";
import PaginatedSearch from "@/mixins/PaginatedSearch";
import NotificationRulesApi from "@/services/api.notificationRule.service";
import { PaginatedSearchQueryParams, UserPermissions } from "@/store/types";
import { Getter } from "vuex-class";
import GenericTable, { TableHeader } from "@/components/table/generic-table.vue";
import { cloneDeep } from 'lodash';
import { truncateString } from '@/filters';
import NotificationRule, { DefaultNotificationRule } from "@/types/sv-data/notification-rule/NotificationRule";
import NotificationRulePagedRequest from "@/types/sv-data/notification-rule/NotificationRulePagedRequest";
import NotificationRuleSetupModal from "./NotificationRuleSetupModal.vue";
import NotificationRuleDeleteModal from "./NotificationRuleDeleteModal.vue";

@Component({
	filters:  { truncateString },
	components: {
		"nav-header": NavHeader,
		"generic-table": GenericTable,
		"notification-rule-setup-modal": NotificationRuleSetupModal,
		"notification-rule-delete-modal": NotificationRuleDeleteModal
	}
})
export default class NotificationRuleSetup extends Mixins(PaginatedSearch) {
	@Getter("getPermissions") private permissions: UserPermissions;
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;
	@Getter getUserTenantGroupId: number;

	private notificationRules: NotificationRule[] = [];
	private selectedNotificationRule: NotificationRule = DefaultNotificationRule;
	private selectedDeleteNotificationRule: NotificationRule | null = null;
	private isLoading: boolean = false;
	private totalRecords: number = 0;
	private isSetupModalShown: boolean = false;
	private isDeleteModalShown: boolean = false;

	private async mounted(): Promise<void> {
		await this.getPaginatedNotificationRules();
	}

	private get isNotificationRulesEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "NotificationRules"]);
	}

	private get hasNotificationRulesPermissions(): boolean {
		return this.permissions.canViewNotificationRules || this.permissions.canEditNotificationRules;
	}

	private get hasEditNotificationRulePermissions(): boolean {
		return this.permissions.canEditNotificationRules;
	}

	private get selectedDeleteTitle(): string {
		return this.selectedDeleteNotificationRule?.title ?? "";
	}

	private async createNotificationRule(notificationRule: NotificationRule): Promise<void> {
		await NotificationRulesApi.createNotificationRule(notificationRule);
		await this.getPaginatedNotificationRules(this.mostRecentSearchParams);
		this.closeSetupModal();
	}

	private async updateNotificationRule(notificationRule: NotificationRule): Promise<void> {
		await NotificationRulesApi.updateNotificationRule(notificationRule);
		await this.getPaginatedNotificationRules(this.mostRecentSearchParams);
		this.closeSetupModal();
	}

	private async deleteNotificationRule(): Promise<void> {
		await NotificationRulesApi.deleteNotificationRule(this.selectedDeleteNotificationRule.notificationRuleId);
		await this.getPaginatedNotificationRules(this.mostRecentSearchParams);
	}

	private setSelectedNotificationRule(rule: NotificationRule | null): void {		
		if (!rule) {
			this.selectedNotificationRule = cloneDeep(DefaultNotificationRule);
		} else {
			this.selectedNotificationRule = rule;
		}
	}

	private async getPaginatedNotificationRules(paginatedSearchQueryParams?: PaginatedSearchQueryParams): Promise<void> {
		this.isLoading = true;
		this.notificationRules = [];

		if (!paginatedSearchQueryParams) {
			paginatedSearchQueryParams = {
				page: 1,
				pageSize: 25,
				sortDesc: false,
				sortBy: "Title",
				searchTerm: ""
			};
		}

		const searchParams = this.generateNewPaginatedSearchRequest(paginatedSearchQueryParams);
		const request = { ...paginatedSearchQueryParams, ...searchParams,  tenantId: this.getUserTenantGroupId } as NotificationRulePagedRequest;

		this.mostRecentSearchParams = request;

		const results = await NotificationRulesApi.getNotificationRules(request);
		this.totalRecords = results.totalRecords;
		this.notificationRules = results.data;

		/* 
			Due to a conflict in naming and the column "actions" (key) already existing in the genericTable a prop by a different name is needed
			to resolve this the actions array of each rule is copied to the 
		*/
		this.notificationRules.forEach((rule: NotificationRule) => rule.ruleActions = cloneDeep(rule.actions));
		this.isLoading = false;
	}

	private get columns(): TableHeader[] {
		const _columns: TableHeader[] = [
			{
				title: "Title",
				key: "title",
				order: 1,
				dataType: "input",
				description: "Notification Rule Title",
				sortable: true,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: true,
				isTermLabel: true
			},
			{
				title: "Conditions",
				key: "conditions",
				order: 2,
				dataType: "list",
				sortable: false,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: true,
				useCustomCell: true
			},
			{
				title: "Actions",
				key: "ruleActions",
				order: 3,
				dataType: "list",
				sortable: false,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: true,
				useCustomCell: true
			}
		];

		return _columns;
	}
	

	private handleEditClick(rule: NotificationRule): void {
		this.selectedNotificationRule = cloneDeep(rule);
		this.isSetupModalShown = true;
	}

	private handleAddClick(): void {
		this.setSelectedNotificationRule(null);
		this.isSetupModalShown = true;
	}

	private handleDeleteClick(rule: NotificationRule): void {
		this.selectedDeleteNotificationRule = rule;
		this.isDeleteModalShown = true;
	}

	private async confirmDeleteNotificationRule(): Promise<void> {
		try {
			await this.deleteNotificationRule();
		} catch(err) {
			console.error(err);
		}finally {
			this.closeDeleteModal();
		}
	}

	private closeDeleteModal(): void {
		this.selectedDeleteNotificationRule = null;
		this.isDeleteModalShown = false;
	}

	private closeSetupModal(): void {
		this.selectedNotificationRule = DefaultNotificationRule;
		this.isSetupModalShown = false;
	}
}
